import React, { useState } from 'react';
import PreferenceHeatmap from '../components/PreferenceHeatmap';

const VisualizationsPage = () => {
  const [activeTab, setActiveTab] = useState('heatmap');

  const tabs = [
    { id: 'heatmap', label: '📊 Preference Heatmap', description: 'Visual preference scoring matrix' },
    { id: 'sunburst', label: '🌐 Persona Sunburst', description: 'Nested category/tags distributions' },
    { id: 'insights', label: '💡 Recommendation Insights', description: 'Why products are recommended' },
    { id: 'analytics', label: '📈 Usage Analytics', description: 'Activity trends over time' }
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'heatmap':
        return (
          <div className="visualization-container">
            <div className="visualization-header">
              <h2>Preference Heatmap</h2>
              <p className="visualization-description">
                This heatmap shows your preference scores across different product categories and subcategories.
                Darker/warmer colors indicate higher preferences (cold blue = low preference, hot red/yellow = high preference).
              </p>
              <div className="visualization-features">
                <div className="feature-item">
                  <span className="feature-icon">🎯</span>
                  <span>Hover over cells to see detailed scores</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">🔥</span>
                  <span>Warm colors = High preferences</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">❄️</span>
                  <span>Cold colors = Low preferences</span>
                </div>
              </div>
            </div>
            <div className="heatmap-wrapper">
              <PreferenceHeatmap width={900} height={700} />
            </div>
            <div className="visualization-insights">
              <h3>Key Insights</h3>
              <div className="insights-grid">
                <div className="insight-card">
                  <h4>🎨 Top Categories</h4>
                  <p>Your highest preferences are in <strong>Beauty</strong> and <strong>Electronics</strong> categories.</p>
                </div>
                <div className="insight-card">
                  <h4>📚 Reading Preferences</h4>
                  <p>You show strong preference for <strong>Fiction</strong> and <strong>Self-Help</strong> books.</p>
                </div>
                <div className="insight-card">
                  <h4>🏃 Activity Interests</h4>
                  <p>Strong interest in <strong>Cardio</strong> and <strong>Water Sports</strong> activities.</p>
                </div>
                <div className="insight-card">
                  <h4>🏠 Home Preferences</h4>
                  <p>You prefer <strong>Kitchen</strong> and <strong>Furniture</strong> over bathroom items.</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'sunburst':
        return (
          <div className="visualization-container">
            <div className="visualization-header">
              <h2>Persona Sunburst Chart</h2>
              <p className="visualization-description">
                Explore how your personas are distributed across different categories and tags.
                Click on segments to drill down into specific areas of interest.
              </p>
            </div>
            <div className="sunburst-placeholder">
              <div className="placeholder-content">
                <div className="placeholder-icon">🌐</div>
                <h3>Interactive Sunburst Coming Soon</h3>
                <p>This visualization will show nested hierarchies of your person preferences and categories.</p>
                <div className="sunburst-features">
                  <div className="feature">🖱️ Click to drill down</div>
                  <div className="feature">📊 See category distributions</div>
                  <div className="feature">👥 Compare across personas</div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'insights':
        return (
          <div className="visualization-container">
            <div className="visualization-header">
              <h2>Recommendation Insights</h2>
              <p className="visualization-description">
                Understand why certain products are recommended to you. See the reasoning behind
                AI-powered suggestions and how your preferences influence recommendations.
              </p>
            </div>
            <div className="insights-placeholder">
              <div className="placeholder-content">
                <div className="placeholder-icon">💡</div>
                <h3>AI Insights Dashboard Coming Soon</h3>
                <p>Transparent AI explanations for every recommendation you receive.</p>
                <div className="insights-preview">
                  <div className="insight-preview-card">
                    <h4>🤖 Why Recommended</h4>
                    <div className="reason-list">
                      <div className="reason">📈 85% match with preferences</div>
                      <div className="reason">💰 Budget alignment: -$25</div>
                      <div className="reason">👥 Friends also purchased</div>
                      <div className="reason">⭐ 4.8+ rating threshold</div>
                    </div>
                  </div>
                  <div className="insight-preview-card">
                    <h4>🎯 Match Analysis</h4>
                    <div className="match-chart">
                      <div className="bar" style={{ width: '85%' }}>Price: 85%</div>
                      <div className="bar" style={{ width: '92%' }}>Category: 92%</div>
                      <div className="bar" style={{ width: '78%' }}>Brand: 78%</div>
                      <div className="bar" style={{ width: '95%' }}>Rating: 95%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'analytics':
        return (
          <div className="visualization-container">
            <div className="visualization-header">
              <h2>Usage Analytics</h2>
              <p className="visualization-description">
                Track your gifting activity over time. See trends in your recommendations,
                wish list creations, and shopping patterns.
              </p>
            </div>
            <div className="analytics-placeholder">
              <div className="placeholder-content">
                <div className="placeholder-icon">📈</div>
                <h3>Activity Trends Coming Soon</h3>
                <p>Comprehensive analytics dashboard for your gifting journey.</p>
                <div className="analytics-preview">
                  <div className="metric-grid">
                    <div className="metric">
                      <div className="metric-value">247</div>
                      <div className="metric-label">Products Browsed</div>
                      <div className="metric-change">↑ +12% this month</div>
                    </div>
                    <div className="metric">
                      <div className="metric-value">18</div>
                      <div className="metric-label">Wish Lists Created</div>
                      <div className="metric-change">↑ +3 this week</div>
                    </div>
                    <div className="metric">
                      <div className="metric-value">92%</div>
                      <div className="metric-label">Click-through Rate</div>
                      <div className="metric-change">↑ +8% this month</div>
                    </div>
                    <div className="metric">
                      <div className="metric-value">4.8</div>
                      <div className="metric-label">Avg Recommendation Score</div>
                      <div className="metric-change">Stable</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ padding: '2rem', paddingTop: '5rem', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h1 style={{
          fontSize: '2.5rem',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          '-webkit-background-clip': 'text',
          '-webkit-text-fill-color': 'transparent',
          backgroundClip: 'text',
          marginBottom: '0.5rem'
        }}>
          Analytics & Insights
        </h1>
        <p style={{
          color: '#666',
          fontSize: '1.1rem',
          maxWidth: '700px',
          margin: '0 auto 2rem'
        }}>
          Discover patterns in your gifting preferences with interactive data visualizations and AI-powered insights.
        </p>
      </div>

      {/* Navigation Tabs */}
      <div className="visualization-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`visualization-tab ${activeTab === tab.id ? 'active' : ''}`}
          >
            <div className="tab-content">
              <div className="tab-label">{tab.label}</div>
              <div className="tab-description">{tab.description}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="visualization-content">
        {renderContent()}
      </div>
    </div>
  );
};

export default VisualizationsPage;
